"use client";

import { LearningRichContent } from "@/components/student/LearningRichContent";
import {
  LessonSectionFrame,
  lessonSectionBodyGapClass,
  lessonSectionBodyProseClass,
  stripSectionNumbering,
} from "@/components/student/LessonSectionFrame";
import { normalizeGeneratedText } from "@/lib/normalize-generated-text";
import { PagedContentSections, type ContentPage } from "@/components/student/PagedContentSections";

/** Proactive lesson JSON shape: title/lesson_title from DB, content in sections + hands_on_project. */
export type ProactiveSection = {
  section_number: number;
  heading: string;
  content: string;
};

export type ProactiveStructuredData = {
  title: string;
  lesson_title: string;
  lesson_type: string;
  personalization_summary: {
    tier: string;
    domain: string;
    career_goal: string;
    learning_objectives: string[];
    prior_struggles_addressed: string[];
  };
  recap_section?: {
    heading: string;
    content: string;
  } | null;
  sections: ProactiveSection[];
  domain_use_case: {
    heading: string;
    scenario: string;
  };
};

function isProactiveStructuredData(v: unknown): v is ProactiveStructuredData {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  if (typeof o.title !== "string") return false;
  if (typeof o.lesson_title !== "string") return false;
  if (typeof o.lesson_type !== "string") return false;
  if (!Array.isArray(o.sections)) return false;
  if (typeof o.personalization_summary !== "object" || o.personalization_summary === null) return false;
  const personalization = o.personalization_summary as Record<string, unknown>;
  if (!Array.isArray(personalization.learning_objectives)) return false;
  if (!Array.isArray(personalization.prior_struggles_addressed)) return false;
  if (typeof o.domain_use_case !== "object" || o.domain_use_case === null) return false;
  return true;
}

interface ProactiveLessonContentProps {
  data: ProactiveStructuredData;
  lessonCompletedInCourse?: boolean;
}

export default function ProactiveLessonContent({
  data,
  lessonCompletedInCourse = false,
}: ProactiveLessonContentProps) {
  const sections = [...data.sections].sort((a, b) => a.section_number - b.section_number);
  const personalization = data.personalization_summary;
  const learningObjectives = personalization.learning_objectives.map((value) => normalizeGeneratedText(value));
  const recap = data.recap_section && typeof data.recap_section === "object" ? data.recap_section : null;
  const duc = (data as unknown as Record<string, unknown>).domain_use_case as unknown;
  const ws = (data as unknown as Record<string, unknown>).workplace_scenario as unknown;
  const ducObj = duc && typeof duc === "object" ? (duc as Record<string, unknown>) : null;
  const wsObj = ws && typeof ws === "object" ? (ws as Record<string, unknown>) : null;

  const useCaseHeading =
    (typeof ducObj?.heading === "string" && normalizeGeneratedText(ducObj.heading)) ||
    (typeof wsObj?.heading === "string" && normalizeGeneratedText(wsObj.heading)) ||
    "Workplace Scenario";

  const useCaseScenario =
    (typeof ducObj?.scenario === "string" && normalizeGeneratedText(ducObj.scenario)) ||
    (typeof wsObj?.scenario === "string" && normalizeGeneratedText(wsObj.scenario)) ||
    (typeof wsObj?.content === "string" && normalizeGeneratedText(wsObj.content)) ||
    (typeof ws === "string" && normalizeGeneratedText(ws)) ||
    "";

  // Sections: heading + markdown+LaTeX so \( X \in \mathbb{R}^{n \times d} \) etc. render correctly.
  // The domain use-case is appended as a final "Workplace Scenario" part, mirroring the
  // old system's trailing "Workplace Scenarios" tab.
  const pages: ContentPage[] = [
    ...sections.map((section, index) => ({
      id: section.section_number,
      label: stripSectionNumbering(section.heading) || `Part ${index + 1}`,
      node: (
        <LessonSectionFrame
          sectionNumber={section.section_number}
          heading={stripSectionNumbering(section.heading)}
        >
          <LearningRichContent
            content={section.content || ""}
            className={lessonSectionBodyProseClass}
            segmentGapClassName={lessonSectionBodyGapClass}
          />
        </LessonSectionFrame>
      ),
    })),
    {
      id: "workplace-scenario",
      label: "Workplace Scenario",
      node: (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center rounded-full bg-emerald-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
              Workplace scenario
            </span>
          </div>
          <h2 className="text-base font-semibold text-slate-900 mt-0">{useCaseHeading}</h2>
          {useCaseScenario ? (
            <p className="text-slate-700 whitespace-pre-line mb-0">{useCaseScenario}</p>
          ) : (
            <p className="text-sm text-slate-500 italic mb-0">
              Personalized workplace scenario will appear here based on your domain and industry focus.
            </p>
          )}
        </section>
      ),
    },
  ];

  return (
    <article className="learning-content max-w-none prose prose-gray max-w-none">

      <section className="mb-8 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center rounded-full bg-indigo-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
          Learning objectives
          </span>
        </div>
        <div className="mb-2">
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            {learningObjectives.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        </div>
      </section>

      {recap && (recap.heading || recap.content) && (
        <section className="mb-8 rounded-xl border border-slate-200 bg-white p-5">
          {recap.heading && <h2 className="text-base font-semibold text-slate-900 mt-0">{recap.heading}</h2>}
          {recap.content && (
            <LearningRichContent
              content={recap.content}
              className="prose prose-slate max-w-none prose-p:text-slate-700 mt-3"
            />
          )}
        </section>
      )}

      <PagedContentSections pages={pages} lessonCompletedInCourse={lessonCompletedInCourse} />
    </article>
  );
}

export { isProactiveStructuredData };
